import { Box, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import AuthContext from '../../store/auth-context';
import FirstStageTable from './StagesTables/FirstStageTable';
import OlympicsContext from '../../store/olympics-context';
import SecondAStageTable from './StagesTables/SecondAStageTable';
import SecondBStageTable from './StagesTables/SecondBStageTable';
import ThirdAStageTable from './StagesTables/ThirdAStageTable';
import ThirdBStageTable from './StagesTables/ThirdBStageTable';
import { useCustomTheme } from '../../store/theme-context';
import StudentsStagesTable from './StudentsStagesTable';
import StageStatistics from './StageStatistics';
import FinalStageRankingTable from './StagesTables/FinalStageRankingTable';
import { useLocation, useSearchParams } from 'react-router-dom';
import { debugLog } from '../../utils/debug';

export const filter = (list: any[], value: any) => {
	return list.filter((i) => {
		const val = Number(value);
		if (!isNaN(val) && value > 0) {
			return i.district.id == val;
		} else {
			return true;
		}
	});
};

export type StageOption = {
	key: number;
	title: string;
};

const StagesTable = () => {
	const { palette } = useTheme();
	const { isHighContrast } = useCustomTheme();
	const { olympics } = useContext(OlympicsContext);
	const [, setSearchParams] = useSearchParams();
	const location = useLocation();
	const { isAdmin } = useContext(AuthContext);
	const isNarrow = useMediaQuery('(max-width:720px)');
	const tabsRef = useRef<HTMLDivElement | null>(null);
	const stageSyncReadyRef = useRef(false);
	const userTabIntentRef = useRef<StageTabKey | null>(null);
	const showStageThirdA = isAdmin || olympics?.thirdAStagePublished;
	const showStageThirdB = isAdmin || olympics?.thirdBStagePublished;
	const showFinalRankingTab = isAdmin && olympics?.thirdAStagePublished;
	type StageTabKey = 'I' | 'IIA' | 'IIB' | 'IIIA' | 'IIIB' | 'ZBIORCZE' | 'LISTA_FINAL' | 'STATYSTYKI';
	const STAGE_TAB_QUERY = 'e';
	const STAGE_TAB_SESSION = 'olympics_panel_stage_tab';
	const tabs = useMemo(
		() =>
			[
				{ key: 'I' as StageTabKey, label: isNarrow ? 'I' : 'Etap I', content: <FirstStageTable /> },
				{ key: 'IIA' as StageTabKey, label: isNarrow ? 'IIA' : 'Etap IIA', content: <SecondAStageTable /> },
				{ key: 'IIB' as StageTabKey, label: isNarrow ? 'IIB' : 'Etap IIB', content: <SecondBStageTable /> },
				...(showStageThirdA
					? [{ key: 'IIIA' as StageTabKey, label: isNarrow ? 'IIIA' : 'Etap IIIA', content: <ThirdAStageTable /> }]
					: []),
				...(showStageThirdB
					? [{ key: 'IIIB' as StageTabKey, label: isNarrow ? 'IIIB' : 'Etap IIIB', content: <ThirdBStageTable /> }]
					: []),
				{
					key: 'ZBIORCZE' as StageTabKey,
					label: isNarrow ? 'ZBIORCZO' : 'Zbiorcze wyniki',
					content: <StudentsStagesTable />,
				},
				...(showFinalRankingTab
					? [
							{
								key: 'LISTA_FINAL' as StageTabKey,
								label: isNarrow ? 'FIN' : 'Lista finałowa',
								content: <FinalStageRankingTable />,
							},
						]
					: []),
				...(isAdmin
					? [
							{
								key: 'STATYSTYKI' as StageTabKey,
								label: isNarrow ? 'STAT' : 'Statystyki',
								content: <StageStatistics />,
							},
						]
					: []),
			] as const,
		[isAdmin, isNarrow, showFinalRankingTab, showStageThirdA, showStageThirdB],
	);
	const defaultTabKey: StageTabKey = tabs[0]?.key ?? 'I';
	const [tabKey, setTabKey] = useState<StageTabKey>(defaultTabKey);
	const handleTabChange = (_event: React.SyntheticEvent, newValue: StageTabKey) => {
		userTabIntentRef.current = newValue;
		setTabKey(newValue);
	};

	useLayoutEffect(() => {
		const params = new URLSearchParams(location.search);
		const fromUrl = (params.get(STAGE_TAB_QUERY) || '').toUpperCase() as StageTabKey;
		const fromSession = (sessionStorage.getItem(STAGE_TAB_SESSION) || '').toUpperCase() as StageTabKey;
		const available = new Set<StageTabKey>(tabs.map((t) => t.key));
		const next = available.has(fromUrl) ? fromUrl : available.has(fromSession) ? fromSession : defaultTabKey;
		const userIntent = userTabIntentRef.current;
		if (userIntent && userIntent === tabKey && fromUrl !== tabKey) {
			// Let state->URL sync finish before applying URL->state.
			stageSyncReadyRef.current = true;
			return;
		}
		if (tabKey !== next) {
			stageSyncReadyRef.current = false;
			debugLog('StagesTable URL->state sync', { fromUrl, fromSession, next, tabKey });
			setTabKey(next);
			return;
		}
		if (userIntent && userIntent === tabKey && fromUrl === tabKey) {
			userTabIntentRef.current = null;
		}
		stageSyncReadyRef.current = true;
	}, [defaultTabKey, location.search, tabKey, tabs]);

	useEffect(() => {
		if (!stageSyncReadyRef.current) {
			return;
		}
		const available = new Set<StageTabKey>(tabs.map((t) => t.key));
		const safe = available.has(tabKey) ? tabKey : defaultTabKey;
		if (safe !== tabKey) {
			stageSyncReadyRef.current = false;
			setTabKey(safe);
			return;
		}
		sessionStorage.setItem(STAGE_TAB_SESSION, safe);
		const params = new URLSearchParams(location.search);
		const current = (params.get(STAGE_TAB_QUERY) || '').toUpperCase();
		if (current !== safe) {
			const nextParams = new URLSearchParams(location.search);
			nextParams.set(STAGE_TAB_QUERY, safe);
			debugLog('StagesTable state->URL sync', {
				safe,
				before: params.toString(),
				after: nextParams.toString(),
			});
			setSearchParams(nextParams, { replace: true });
		}
	}, [defaultTabKey, location.search, setSearchParams, tabKey, tabs]);

	useEffect(() => {
		if (!tabsRef.current) {
			return;
		}
		const element = tabsRef.current;
		const updateHeight = () => {
			const height = Math.ceil(element.getBoundingClientRect().height);
			document.documentElement.style.setProperty('--panel-stages-tabs-height', `${height}px`);
		};
		updateHeight();
		const observer = new ResizeObserver(updateHeight);
		observer.observe(element);
		return () => observer.disconnect();
	}, []);

	return (
		<>
			<Box
				ref={tabsRef}
				sx={{
					borderBottom: 1,
					mt: '10px',
					borderColor: 'divider',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
					}}
				>
					{isNarrow && (
						<Typography
							variant='subtitle2'
							sx={{ color: palette.primary.main, whiteSpace: 'nowrap' }}
						>
							ETAP:
						</Typography>
					)}
					<Tabs
						value={tabKey}
						onChange={handleTabChange}
						variant={isNarrow ? 'scrollable' : 'standard'}
						scrollButtons={isNarrow ? 'auto' : undefined}
						allowScrollButtonsMobile={isNarrow ? true : undefined}
					>
						{tabs.map((tab) => (
							<Tab
								key={tab.key}
								value={tab.key}
								label={tab.label}
								sx={{
									color: isHighContrast ? palette.primary.main : 'inherit',
									minWidth: '50px',
								}}
							/>
						))}
					</Tabs>
				</Box>
			</Box>
			{tabs.find((tab) => tab.key === tabKey)?.content ?? tabs[0]?.content}
		</>
	);
};

export default StagesTable;
